import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { isArray } from "lodash";

import {
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  X as LucideX,
  HelpCircle,
  Info,
  Pipette,
} from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import ColorBoxModal from "../ColorBoxModal";
import CustomToggleSwitch from "../CustomToggleSwitch";
import MessageVariablesPicker from "../MessageVariablesPicker";
import SchedulesForm from "../SchedulesForm";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    dialogPaper: {
      ...(ds.dialogPaper || {}),
      background: isDark ? "#121212" : "#ffffff",
    },
    dialogContent: {
      padding: "16px 20px !important",
      ...theme.scrollbarStyles,
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(244, 67, 54, 0.1)",
      color: ds.closeButton?.color || "#f44336",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    tabsPaper: {
      flexShrink: 0,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
      borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      borderRadius: 0,
      boxShadow: "none",
    },
    tabs: {
      minHeight: 42,
      "& .MuiTab-root": {
        minHeight: 42,
        textTransform: "none",
        fontSize: 13,
        fontWeight: 500,
        minWidth: "auto",
        padding: "6px 18px",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        "&.Mui-selected": {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      },
      "& .MuiTabs-indicator": {
        height: 2,
        borderRadius: 2,
      },
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      marginTop: 16,
      marginBottom: 4,
    },
    sectionDescription: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 8,
      lineHeight: 1.5,
    },
    infoBox: {
      marginTop: 4,
      marginBottom: 14,
      padding: "12px 14px",
      borderRadius: 10,
      background: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
      border: `1px solid ${isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.20)"}`,
    },
    infoTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#90caf9" : "#1565c0",
      marginBottom: 6,
    },
    infoText: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
      lineHeight: 1.55,
      "& strong": {
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
      "& ul": {
        margin: "4px 0 4px 18px",
        padding: 0,
      },
      "& li": {
        marginBottom: 2,
      },
    },
    fieldWithHelp: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      width: "100%",
    },
    fieldWithHelpInner: {
      flex: 1,
      minWidth: 0,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      cursor: "help",
      opacity: 0.6,
      marginLeft: 4,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    toggleCard: {
      display: "flex",
      flexDirection: "column",
      padding: "10px 16px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      marginBottom: 8,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.05)"
          : "#f5f5f5",
        borderColor: isDark
          ? "rgba(255,255,255,0.20)"
          : "#bdbdbd",
      },
    },
    toggleCardRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: 12,
    },
    toggleExpanded: {
      marginTop: 10,
      paddingTop: 10,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },
    toggleLabel: {
      fontSize: "0.9rem",
      color: isDark ? "#e9edef" : "#424242",
      fontWeight: 500,
    },
    toggleLabelWithHelp: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    varsPickerWrap: {
      marginTop: 8,
    },
    colorAdorment: {
      width: 20,
      height: 20,
      borderRadius: 4,
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
    },
    schedulesTabContainer: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },
  };
});

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("queueModal.validations.tooShort"))
    .max(50, i18n.t("queueModal.validations.tooLong"))
    .required(i18n.t("queueModal.validations.required")),
  color: Yup.string()
    .min(3, i18n.t("queueModal.validations.tooShort"))
    .max(9, i18n.t("queueModal.validations.tooLong"))
    .required(),
  greetingMessage: Yup.string(),
});

// Helper: campo + ícone de ajuda à direita
const FieldWithHelp = ({ classes, help, children }) => (
  <div className={classes.fieldWithHelp}>
    <div className={classes.fieldWithHelpInner}>{children}</div>
    {help && (
      <Tooltip arrow placement="top" title={help}>
        <span className={classes.helpIcon}>
          <HelpCircle size={16} strokeWidth={2} />
        </span>
      </Tooltip>
    )}
  </div>
);

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();

  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    outOfHoursMessage: "",
    tempoRoteador: 0,
    ativarRoteador: false,
    closeTicket: false,
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const greetingRef = useRef();
  const greetingMessageInputRef = useRef();

  // Insere a variável escolhida pelo MessageVariablesPicker no campo de saudação
  // na posição atual do cursor.
  const handleClickMsgVar = async (msgVar, setFieldValue) => {
    const el = greetingMessageInputRef.current;
    if (!el) {
      setFieldValue("greetingMessage", `${msgVar}`);
      return;
    }
    const firstHalf = el.value.substring(0, el.selectionStart);
    const secondHalf = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setFieldValue("greetingMessage", `${firstHalf}${msgVar}${secondHalf}`);
    await new Promise((r) => setTimeout(r, 100));
    if (greetingMessageInputRef.current) {
      greetingMessageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      greetingMessageInputRef.current.focus();
    }
  };
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [tab, setTab] = useState(0);
  const { user } = useContext(AuthContext);
  const { get: getSetting } = useCompanySettings();

  const initialStateSchedule = [
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
  ];

  const [schedules, setSchedules] = useState(initialStateSchedule);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const setting = await getSetting({ column: "scheduleType" });
        setSchedulesEnabled(setting?.scheduleType === "queue");
      } catch (err) {
        setSchedulesEnabled(false);
      }
    })();
  }, [open, getSetting]);

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prevState) => ({ ...prevState, ...data }));
        if (isArray(data.schedules) && data.schedules.length > 0) {
          setSchedules(data.schedules);
        }
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueId, open]);

  const handleClose = () => {
    onClose();
    setTab(0);
  };

  const handleSaveSchedules = (data, extras = {}) => {
    setSchedules(data);
    if (typeof extras.outOfHoursMessage === "string") {
      setQueue((prev) => ({ ...prev, outOfHoursMessage: extras.outOfHoursMessage }));
    }
    setTab(0);
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, { ...values, schedules });
      } else {
        await api.post("/queue", { ...values, schedules });
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {queueId
            ? i18n.t("queueModal.title.edit")
            : i18n.t("queueModal.title.add")}
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          className={classes.closeButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      {schedulesEnabled && (
        <div className={classes.tabsPaper}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, v) => setTab(v)}
            className={classes.tabs}
          >
            <Tab label={i18n.t("queueModal.title.queueData")} />
            <Tab label={i18n.t("queueModal.title.text") || "Horário de expediente"} />
          </Tabs>
        </div>
      )}

      {tab === 0 && (
        <Formik
          initialValues={queue}
          validateOnChange={false}
          enableReinitialize={true}
          validationSchema={QueueSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQueue(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ setFieldValue, touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {/* INFO BOX */}
                <div className={classes.infoBox}>
                  <div className={classes.infoTitle}>
                    <Info size={15} strokeWidth={2.2} />
                    {i18n.t("queueModal.info.intro.title") || "ⓘ O que é uma fila"}
                  </div>
                  <div className={classes.infoText}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          i18n.t("queueModal.info.intro.text") ||
                          "<strong>Filas</strong> (departamentos) organizam o atendimento. Cada conexão pode ser vinculada a uma ou mais filas, e os atendentes só veem tickets das filas em que são membros.<ul><li>Defina <strong>nome</strong> e <strong>cor</strong> para identificar a fila visualmente.</li><li>A <strong>mensagem de saudação</strong> é enviada quando o cliente entra nessa fila.</li><li>O <strong>roteador</strong> distribui automaticamente tickets entre os atendentes online.</li></ul><strong>Para automação avançada</strong>, use o <em>FlowBuilder</em> e selecione o fluxo na conexão WhatsApp.",
                      }}
                    />
                  </div>
                </div>

                {/* SEÇÃO: IDENTIFICAÇÃO */}
                <div className={classes.sectionTitle}>
                  {i18n.t("queueModal.sections.identity") || "Identificação"}
                </div>
                <div className={classes.sectionDescription}>
                  {i18n.t("queueModal.sections.identityDesc") ||
                    "Nome, cor e ordem de exibição da fila."}
                </div>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <FieldWithHelp
                      classes={classes}
                      help={
                        i18n.t("queueModal.help.name") ||
                        "Nome interno da fila. Aparece em listas e relatórios. Use algo descritivo (ex: 'Suporte', 'Vendas BR')."
                      }
                    >
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.name")}
                        autoFocus
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </FieldWithHelp>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FieldWithHelp
                      classes={classes}
                      help={
                        i18n.t("queueModal.help.color") ||
                        "Cor do tag visual da fila. Aparece nas listas de tickets, no chat e nos relatórios."
                      }
                    >
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.color")}
                        name="color"
                        id="color"
                        onFocus={() => {
                          setColorPickerModalOpen(true);
                          if (greetingRef.current) greetingRef.current.focus();
                        }}
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <div
                                style={{ backgroundColor: values.color }}
                                className={classes.colorAdorment}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
                            >
                              <Pipette size={16} strokeWidth={2} />
                            </IconButton>
                          ),
                        }}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </FieldWithHelp>
                  </Grid>
                </Grid>

                <ColorBoxModal
                  open={colorPickerModalOpen}
                  handleClose={() => setColorPickerModalOpen(false)}
                  onChange={(color) => {
                    const hexColor =
                      typeof color === "string"
                        ? color.startsWith("#")
                          ? color
                          : `#${color}`
                        : `#${color.hex}`;
                    setFieldValue("color", hexColor);
                  }}
                  currentColor={values.color}
                />

                {/* SEÇÃO: COMPORTAMENTO */}
                <div className={classes.sectionTitle}>
                  {i18n.t("queueModal.sections.behavior") || "Comportamento"}
                </div>
                <div className={classes.sectionDescription}>
                  {i18n.t("queueModal.sections.behaviorDesc") ||
                    "Define ações automáticas quando o ticket entra ou é atribuído nesta fila."}
                </div>

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <div className={classes.toggleCard}>
                      <div className={classes.toggleCardRow}>
                        <span className={classes.toggleLabelWithHelp}>
                          <span className={classes.toggleLabel}>
                            {i18n.t("queueModal.form.closeTicket")}
                          </span>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              i18n.t("queueModal.help.closeTicket") ||
                              "Quando ativado, o ticket é encerrado automaticamente ao final do fluxo do bot."
                            }
                          >
                            <span className={classes.helpIcon}>
                              <HelpCircle size={14} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </span>
                        <CustomToggleSwitch
                          checked={!!values.closeTicket}
                          onChange={(e) =>
                            setFieldValue("closeTicket", e.target.checked)
                          }
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.toggleCard}>
                      <div className={classes.toggleCardRow}>
                        <span className={classes.toggleLabelWithHelp}>
                          <span className={classes.toggleLabel}>
                            {i18n.t("queueModal.form.rotate")}
                          </span>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              i18n.t("queueModal.help.rotate") ||
                              "Roteador automático: redistribui tickets sem atendente entre os agentes online da fila."
                            }
                          >
                            <span className={classes.helpIcon}>
                              <HelpCircle size={14} strokeWidth={2} />
                            </span>
                          </Tooltip>
                        </span>
                        <CustomToggleSwitch
                          checked={!!values.ativarRoteador}
                          onChange={(e) =>
                            setFieldValue("ativarRoteador", e.target.checked)
                          }
                        />
                      </div>
                      {values.ativarRoteador && (
                        <div className={classes.toggleExpanded}>
                          <FieldWithHelp
                            classes={classes}
                            help={
                              i18n.t("queueModal.help.tempoRoteador") ||
                              "Intervalo (em minutos) entre cada redistribuição automática. Recomendado: 5 a 10 minutos."
                            }
                          >
                            <FormControl variant="outlined" margin="dense" fullWidth>
                              <InputLabel id="tempoRoteador-label">
                                {i18n.t("queueModal.form.timeRotate")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("queueModal.form.timeRotate")}
                                name="tempoRoteador"
                                id="tempoRoteador"
                                labelId="tempoRoteador-label"
                                value={values.tempoRoteador || 0}
                                MenuProps={{
                                  classes: { paper: dropdownClasses.paper },
                                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                  transformOrigin: { vertical: "top", horizontal: "left" },
                                  getContentAnchorEl: null,
                                }}
                              >
                                <MenuItem value={0} disabled>
                                  {i18n.t("queueModal.form.timeRotate")}
                                </MenuItem>
                                <MenuItem value={2}>{i18n.t("queueModal.form.timeOptions.minutes2")}</MenuItem>
                                <MenuItem value={5}>{i18n.t("queueModal.form.timeOptions.minutes5")}</MenuItem>
                                <MenuItem value={10}>{i18n.t("queueModal.form.timeOptions.minutes10")}</MenuItem>
                                <MenuItem value={15}>{i18n.t("queueModal.form.timeOptions.minutes15")}</MenuItem>
                                <MenuItem value={30}>{i18n.t("queueModal.form.timeOptions.minutes30")}</MenuItem>
                                <MenuItem value={45}>{i18n.t("queueModal.form.timeOptions.minutes45")}</MenuItem>
                                <MenuItem value={60}>{i18n.t("queueModal.form.timeOptions.minutes60")}</MenuItem>
                              </Field>
                            </FormControl>
                          </FieldWithHelp>
                        </div>
                      )}
                    </div>
                  </Grid>
                </Grid>

                {/* SEÇÃO: MENSAGENS */}
                <div className={classes.sectionTitle}>
                  {i18n.t("queueModal.sections.messages") || "Mensagens automáticas"}
                </div>
                <div className={classes.sectionDescription}>
                  {i18n.t("queueModal.sections.messagesDesc") ||
                    "Textos enviados automaticamente em momentos específicos do atendimento."}
                </div>

                <FieldWithHelp
                  classes={classes}
                  help={
                    i18n.t("queueModal.help.greetingMessage") ||
                    "Texto enviado ao cliente quando o ticket entra nesta fila. Use o seletor de variáveis abaixo para inserir dados do contato/atendente automaticamente."
                  }
                >
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    multiline
                    inputRef={(el) => {
                      greetingRef.current = el;
                      greetingMessageInputRef.current = el;
                    }}
                    minRows={3}
                    fullWidth
                    name="greetingMessage"
                    error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                    helperText={touched.greetingMessage && errors.greetingMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </FieldWithHelp>
                <div className={classes.varsPickerWrap}>
                  <MessageVariablesPicker
                    disabled={isSubmitting}
                    onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                  />
                </div>
              </DialogContent>

              <ChatiaDialogActions
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                saveText={
                  queueId
                    ? i18n.t("queueModal.buttons.okEdit")
                    : i18n.t("queueModal.buttons.okAdd")
                }
              />
            </Form>
          )}
        </Formik>
      )}

      {tab === 1 && schedulesEnabled && (
        <div className={classes.schedulesTabContainer}>
          <SchedulesForm
            loading={false}
            initialValues={schedules}
            showOutOfHoursMessage
            initialOutOfHoursMessage={queue.outOfHoursMessage || ""}
            labelSaveButton={i18n.t("queueModal.buttons.okEdit")}
            onSubmit={handleSaveSchedules}
          />
        </div>
      )}
    </Dialog>
  );
};

export default QueueModal;
